import { BrandAsset, BrandAssetKind, LogoType } from "../../../shared/models/brand.model";
import type { BrandAssetFields } from "../../../shared/services/organizations/commands/cmdCreateBrandAsset";

/*
    The brand's assets as the Branding tab edits them (MPN-1712). Each kind is its own
    section with its own save, so the form holds every kind and a save sends one.
*/

/** An asset on the page. `key` names the saved asset; an added one has a key and a file, and no id until saved. */
export type BrandAssetEntry = Omit<BrandAsset, "id"> & {
    id: number | null;
    key: string;
    file?: File;
    /** Why the last save of this asset failed, e.g. the server's 409 naming the logo that holds the slot. */
    error?: string;
};

/** What each section's save calls its assets. */
export const ASSET_KIND_LABELS: Record<BrandAssetKind, string> = {
    LOGO: "Logos",
    ICON: "Icons",
    FAVICON: "Favicon",
    GRAPHIC_PATTERN: "Graphics/Patterns",
    LIBRARY: "Image/Video Library",
};

/** One live asset of these logo types per brand; the server answers a second with a 409. */
export const UNIQUE_LOGO_TYPES: LogoType[] = ["PRIMARY", "SQUARE_FORMAT"];

const DETAIL_FIELDS = ["kind", "logo_type", "name", "description", "minimum_size", "usage_guidelines"] as const;

const blankToNull = <T>(value: T | "") => (value === "" ? null : value);

export const asAssetEntry = (asset: BrandAsset): BrandAssetEntry => ({ ...asset, key: `asset-${asset.id}` });

/** A file picked on the page, not uploaded until its section is saved. */
export const newAssetEntry = (key: string, kind: BrandAssetKind, file: File): BrandAssetEntry => ({
    key,
    id: null,
    file,
    kind,
    tag: null,
    logo_type: null,
    name: null,
    url: null,
    description: null,
    minimum_size: null,
    usage_guidelines: null,
    media_type: file.type.startsWith("video/") ? "video" : "image",
    width: null,
    height: null,
});

/** The PATCH body for one asset: only what differs from what was saved, with "" as null. */
export const assetChanges = (saved: BrandAssetEntry, form: BrandAssetEntry): BrandAssetFields => {
    const changes: Record<string, string | null> = {};
    for (const field of DETAIL_FIELDS) {
        if (blankToNull(form[field]) !== blankToNull(saved[field])) changes[field] = blankToNull(form[field]);
    }
    return changes as BrandAssetFields;
};

/** The upload's form fields for an added asset. Blank text is left out. */
export const newAssetFields = (entry: BrandAssetEntry): BrandAssetFields => {
    const fields: Record<string, string> = {};
    for (const field of DETAIL_FIELDS) {
        const value = entry[field];
        if (value) fields[field] = value;
    }
    return fields as BrandAssetFields;
};

/** True when saving `changes` frees a one-per-brand slot, so it has to go before an edit that takes it. */
export const vacatesSlot = (saved: BrandAssetEntry, changes: BrandAssetFields) =>
    "logo_type" in changes && !!saved.logo_type && UNIQUE_LOGO_TYPES.includes(saved.logo_type);

/** Which saved asset the server's answer settles: the one just updated, or the added entry just uploaded. */
export type AssetSettle = { id?: number; key?: string };

/**
 * The form once the server has `next`. An asset nobody has touched takes the server's
 * version. An edited one keeps the edits, field by field, and takes everything else (its
 * file, and anything the server changed that wasn't edited, like a Primary demoted by "set
 * as default"). Removed and added entries stay as they are.
 *
 * `settle` names an asset whose save this answer is: it takes the server's version whole,
 * so text the server trimmed doesn't read as an unsaved edit, and an uploaded entry
 * becomes the asset the server made of it.
 */
export const rebaseAssets = (
    form: BrandAssetEntry[],
    prevSaved: BrandAssetEntry[],
    next: BrandAsset[],
    settle: AssetSettle = {},
): BrandAssetEntry[] => {
    const nextEntries = next.map(asAssetEntry);
    const known = new Set(prevSaved.map((s) => s.id));
    const created = settle.key ? nextEntries.find((n) => !known.has(n.id)) : undefined;

    const rebased = form.flatMap((entry): BrandAssetEntry[] => {
        if (entry.id === null) return [created && entry.key === settle.key ? created : entry];
        const server = nextEntries.find((n) => n.id === entry.id);
        if (!server) return [];
        const before = prevSaved.find((s) => s.id === entry.id);
        if (!before || entry.id === settle.id) return [server];
        const merged: BrandAssetEntry = { ...server, key: entry.key };
        for (const field of DETAIL_FIELDS) {
            if (entry[field] !== before[field]) (merged as Record<string, unknown>)[field] = entry[field];
        }
        return [merged];
    });

    const inForm = new Set(rebased.map((e) => e.id));
    const removed = new Set(prevSaved.filter((s) => !form.some((e) => e.id === s.id)).map((s) => s.id));
    const arrived = nextEntries.filter((n) => !inForm.has(n.id) && !removed.has(n.id));
    return [...rebased, ...arrived];
};
